/**
 * API Client for the Nu Trust System
 * 
 * This module provides a centralized client for interacting with the backend API.
 * It handles authentication, request formatting, and response parsing.
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { setupTokenRefreshInterceptor } from '../utils/tokenRefreshInterceptor';

// Define API base URL from environment or default to relative path
// In production (HTTPS), this ensures all API calls use HTTPS through nginx proxy
// In development, this calls localhost:3000/api which proxy redirects to localhost:8003
const API_BASE_URL = (window as any).env?.REACT_APP_API_URL || '/api';

// Get API key from environment
const API_KEY = (window as any).env?.REACT_APP_CONTENT_API_KEY || 'dev-api-key-2024';

// Configure axios defaults
const axiosConfig: AxiosRequestConfig = {
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  },
};

/**
 * Base ApiClient class that wraps axios
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create(axiosConfig);

    // Setup automatic token refresh interceptor
    setupTokenRefreshInterceptor(this.client);

    // Add response interceptor for general error handling (after token refresh)
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle common errors here (server errors, etc.)
        // Note: 401/403 are already handled by token refresh interceptor
        if (error.response) {
          const status = error.response.status;

          // Extract detail message from FastAPI error responses
          if (error.response.data && error.response.data.detail) {
            const detail = error.response.data.detail;
            if (Array.isArray(detail)) {
              // Pydantic validation errors return an array of {type, loc, msg, input, ctx}
              error.message = detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
            } else if (typeof detail === 'string') {
              error.message = detail;
            } else {
              error.message = JSON.stringify(detail);
            }
          }

          if (status >= 500) {
            console.error('Server error', error);
            // Show server error notification
          }
        } else if (error.request) {
          console.error('Network error', error);
          // Show network error notification
        } else {
          console.error('Unknown error', error);
          // Show generic error notification
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Generic GET request
   */
  async get<T>(endpoint: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(endpoint, { params });
    return response.data;
  }

  /**
   * Generic POST request
   */
  async post<T>(endpoint: string, data: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(endpoint, data);
    return response.data;
  }

  /**
   * Generic PUT request
   */
  async put<T>(endpoint: string, data: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(endpoint, data);
    return response.data;
  }

  /**
   * Generic DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(endpoint);
    return response.data;
  }

  /**
   * Generic PATCH request
   */
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(endpoint, data);
    return response.data;
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
export default apiClient;
